/**
 * Analytics v2 — structured-query catalog (NEX-2478).
 *
 * Single source of truth for which curated views, columns, dimensions and
 * measures the structured (non-SQL) query API exposes. The SQL builder emits
 * ONLY identifiers drawn from this catalog, so a request can never inject an
 * arbitrary table/column name. The same catalog is what the dashboard UI (L4)
 * and the universal Score object (L2) will introspect to render pickers.
 *
 * Keys are the PUBLIC view names used in the API (`conversations`); `physical`
 * is the underlying database view (`analytics_conversations`). Every column is
 * tagged with a SQL value class (`type`) used to coerce/escape filter values,
 * and a `kind`:
 *   - `dimension` — groupable and filterable
 *   - `measure`   — numeric, aggregatable (sum/avg/min/max) and filterable
 *   - `timestamp` — filterable; one per view is the default activity timestamp
 */

/** SQL value class of a column — drives filter-value coercion and escaping. */
export type AnalyticsColumnType = "text" | "number" | "boolean" | "timestamp";

/** Role a column can play in a structured query. */
export type AnalyticsColumnKind = "dimension" | "measure" | "timestamp";

export interface AnalyticsColumnDef {
  readonly type: AnalyticsColumnType;
  readonly kind: AnalyticsColumnKind;
  /**
   * Set on a dimension that is the human-readable LABEL of another dimension —
   * the value is the column it labels (e.g. `workflowName.labelFor` is
   * `"workflowId"`).
   *
   * The id stays the thing being grouped: two workflows may share a name, so
   * grouping by the name alone would silently merge them into one row. A
   * consumer groups by BOTH and treats the pair as a single dimension —
   * displaying the label, keeping the id to drill through.
   */
  readonly labelFor?: string;
}

export interface AnalyticsViewDef {
  /** Underlying database view name (analytics_*). */
  readonly physical: string;
  /** Column filtered by `period` and bucketed by `granularity` (must be kind=timestamp). */
  readonly activityTs: string;
  readonly columns: Readonly<Record<string, AnalyticsColumnDef>>;
}

const dim = (type: AnalyticsColumnType): AnalyticsColumnDef => ({ type, kind: "dimension" });
/**
 * Where a row came from, and whether that is known.
 *
 * `deploymentId` and `deploymentAttribution` are declared together on every fact
 * view that carries them, because a NULL id on its own conflates two opposite
 * answers: "this row genuinely has no deployment" and "nothing has resolved one
 * yet". The database states the same thing as a CHECK — `ATTRIBUTED` holds if and
 * only if the id is non-null — so exposing the id without the enum would let a
 * reader draw a confident conclusion from an absence the enum would have
 * qualified.
 *
 * The enum is typed `text` rather than given a class of its own: filter values
 * are emitted as bare scalar literals, and Postgres resolves `'ATTRIBUTED'`
 * against the enum type unaided. `analytics-view-deployment-attribution.integration.spec.ts`
 * proves that, and proves an unknown label errors rather than matching nothing.
 */
const deploymentProvenance = {
  deploymentId: dim("text"),
  deploymentAttribution: dim("text")
} as const;

/** A text dimension that is the display label of `labelFor`. */
const label = (labelFor: string): AnalyticsColumnDef => ({
  type: "text",
  kind: "dimension",
  labelFor
});
const measure: AnalyticsColumnDef = { type: "number", kind: "measure" };
const ts: AnalyticsColumnDef = { type: "timestamp", kind: "timestamp" };

export const ANALYTICS_CATALOG = {
  generations: {
    physical: "analytics_generations",
    activityTs: "startedAt",
    columns: {
      provider: dim("text"),
      modelName: dim("text"),
      status: dim("text"),
      finishReason: dim("text"),
      isAborted: dim("boolean"),
      deploymentId: dim("text"),
      deploymentSessionId: dim("text"),
      workflowExecutionId: dim("text"),
      agentId: dim("text"),
      customerId: dim("text"),
      traceId: dim("text"),
      inputTokens: measure,
      outputTokens: measure,
      cacheReadInputTokens: measure,
      cacheCreationInputTokens: measure,
      reasoningTokens: measure,
      costInUSDTenThousandths: measure,
      costUsd: measure,
      durationMs: measure,
      thinkingDurationMs: measure,
      ttftMs: measure,
      startedAt: ts,
      completedAt: ts
    }
  },
  traces: {
    physical: "analytics_traces",
    activityTs: "startedAt",
    columns: {
      status: dim("text"),
      deploymentId: dim("text"),
      deploymentSessionId: dim("text"),
      workflowExecutionId: dim("text"),
      agentId: dim("text"),
      customerId: dim("text"),
      triggeredByUserId: dim("text"),
      costUsd: measure,
      totalInputTokens: measure,
      totalOutputTokens: measure,
      totalDurationMs: measure,
      startedAt: ts,
      completedAt: ts
    }
  },
  conversations: {
    physical: "analytics_conversations",
    activityTs: "lastMessageAt",
    columns: {
      agentId: dim("text"),
      ...deploymentProvenance,
      deploymentSessionId: dim("text"),
      status: dim("text"),
      ticketStatus: dim("text"),
      responseHandling: dim("text"),
      type: dim("text"),
      topic: dim("text"),
      isEmulated: dim("boolean"),
      parentChatId: dim("text"),
      messageCount: measure,
      humanMessageCount: measure,
      aiMessageCount: measure,
      createdAt: ts,
      closedAt: ts,
      lastMessageUpdatedAt: ts,
      firstMessageAt: ts,
      lastMessageAt: ts
    }
  },
  messages: {
    physical: "analytics_messages",
    activityTs: "createdAt",
    columns: {
      chatId: dim("text"),
      agentId: dim("text"),
      ...deploymentProvenance,
      userId: dim("text"),
      senderIdentityId: dim("text"),
      runId: dim("text"),
      type: dim("text"),
      status: dim("text"),
      deliveryStatus: dim("text"),
      reasoningDurationMs: measure,
      createdAt: ts
    }
  },
  executions: {
    physical: "analytics_executions",
    activityTs: "createdAt",
    columns: {
      workflowId: dim("text"),
      workflowName: label("workflowId"),
      ...deploymentProvenance,
      runId: dim("text"),
      status: dim("text"),
      flowFinished: dim("boolean"),
      wasTestExecution: dim("boolean"),
      triggerMessageId: dim("text"),
      parentNodeId: dim("text"),
      durationMs: measure,
      createdAt: ts,
      executionStartedAt: ts,
      executionStoppedAt: ts,
      finishedAt: ts
    }
  },
  node_runs: {
    physical: "analytics_node_runs",
    activityTs: "createdAt",
    columns: {
      workflowExecutionId: dim("text"),
      ...deploymentProvenance,
      nodeId: dim("text"),
      label: dim("text"),
      nodeType: dim("text"),
      status: dim("text"),
      errorMessage: dim("text"),
      isRetryable: dim("boolean"),
      retryCount: measure,
      durationMs: measure,
      createdAt: ts,
      finishedAt: ts
    }
  },
  scores: {
    physical: "analytics_scores",
    activityTs: "createdAt",
    columns: {
      agentId: dim("text"),
      agentName: label("agentId"),
      chatId: dim("text"),
      deploymentId: dim("text"),
      messageId: dim("text"),
      framework: dim("text"),
      source: dim("text"),
      score: measure,
      rawScore: measure,
      createdAt: ts
    }
  },
  // Universal Score object (NEX-2498) — append-only score emissions. Distinct
  // from `scores` (SatisfactionScore): any emitter, any scorable entity, a
  // numeric / categorical / boolean value. Backed by analytics_score_events.
  score_events: {
    physical: "analytics_score_events",
    activityTs: "createdAt",
    columns: {
      name: dim("text"),
      valueType: dim("text"),
      scorableType: dim("text"),
      scorableId: dim("text"),
      ...deploymentProvenance,
      emitterType: dim("text"),
      emitterId: dim("text"),
      emitterName: dim("text"),
      categoricalValue: dim("text"),
      booleanValue: dim("boolean"),
      numericValue: measure,
      createdAt: ts
    }
  }
} as const satisfies Record<string, AnalyticsViewDef>;

export type AnalyticsViewName = keyof typeof ANALYTICS_CATALOG;

/** Public view names as a tuple for `z.enum`. */
export const ANALYTICS_VIEW_NAMES = Object.keys(ANALYTICS_CATALOG) as [
  AnalyticsViewName,
  ...AnalyticsViewName[]
];

/** Aggregations allowed on measures (count(*) is handled separately). */
export const ANALYTICS_AGGREGATIONS = ["sum", "avg", "min", "max"] as const;
export type AnalyticsAggregation = (typeof ANALYTICS_AGGREGATIONS)[number];

/** Filter operators. `in` takes an array; the rest take a scalar. */
export const ANALYTICS_FILTER_OPS = ["eq", "neq", "in", "gt", "gte", "lt", "lte"] as const;
export type AnalyticsFilterOp = (typeof ANALYTICS_FILTER_OPS)[number];

/** Time-bucket granularities for `granularity`. */
export const ANALYTICS_GRANULARITIES = ["hour", "day", "week", "month"] as const;
export type AnalyticsGranularity = (typeof ANALYTICS_GRANULARITIES)[number];

const viewDef = (view: AnalyticsViewName): AnalyticsViewDef => ANALYTICS_CATALOG[view];

/** Column definition, or undefined if the column is not in the view. */
export function analyticsColumn(
  view: AnalyticsViewName,
  column: string
): AnalyticsColumnDef | undefined {
  return viewDef(view).columns[column];
}

export function analyticsDimensions(view: AnalyticsViewName): string[] {
  return Object.entries(viewDef(view).columns)
    .filter(([, c]) => c.kind === "dimension")
    .map(([name]) => name);
}

export function analyticsMeasures(view: AnalyticsViewName): string[] {
  return Object.entries(viewDef(view).columns)
    .filter(([, c]) => c.kind === "measure")
    .map(([name]) => name);
}

/**
 * Dimensions a user should CHOOSE to group by — every dimension except the ones
 * that exist purely to label another (`labelFor`).
 *
 * A label is still a valid `groupBy` value (`analyticsDimensions` includes it,
 * and the request schema validates against that) because it must be selectable
 * ALONGSIDE the id it labels. It just should not be offered on its own: two
 * workflows can share a name, so grouping by the name alone silently merges
 * them into one row. Pickers use this; validation uses `analyticsDimensions`.
 */
export function analyticsGroupableDimensions(view: AnalyticsViewName): string[] {
  return Object.entries(viewDef(view).columns)
    .filter(([, c]) => c.kind === "dimension" && c.labelFor === undefined)
    .map(([name]) => name);
}

/**
 * Map of id-dimension → its label column, for every labelled dimension in the
 * view (e.g. `{ workflowId: "workflowName" }`).
 *
 * The pairing is declared once in the catalog and read from here, so a consumer
 * never hardcodes "workflowName labels workflowId" — a renamed column changes
 * one entry above and every consumer follows.
 */
export function analyticsLabelColumns(view: AnalyticsViewName): Record<string, string> {
  const pairs: Record<string, string> = {};
  for (const [name, def] of Object.entries(viewDef(view).columns)) {
    if (def.labelFor !== undefined) pairs[def.labelFor] = name;
  }
  return pairs;
}
